import PropTypes from 'prop-types';
import classNames from 'classnames';
import {useMemo} from "react";

const DEFAULT_OPTIONS = [
    {label: "Excellent", value: 5},
    {label: "Great", value: 4},
    {label: "OK", value: 3},
    {label: "Poor", value: 2},
    {label: "Very Poor", value: 1},
];

const getOptionValue = (option) => {
    if (option.value === undefined || option.value === null || option.value === '') return option.label;
    if (option.label) return `${option.value} (${option.label})`;
    return option.value;
};

export default function Rating(props) {
    const options = useMemo(() => {
        const configured = Array.isArray(props.options) && props.options.length ? props.options : DEFAULT_OPTIONS;
        return configured.map(option => ({
            ...option,
            value: getOptionValue(option),
            numericValue: Number(option.value)
        }));
    }, [props.options]);
    const selectedIndex = options.findIndex(option => `${option.value}` === `${props.value}`);
    const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

    return (
        <div className={classNames("flex flex-col gap-1", props.className)}>
            <input
                id={props.id}
                className="sr-only"
                type="text"
                name={props.name}
                value={props.value || ''}
                required={props.required}
                disabled={props.disabled}
                data-error-message={props.errorMessage}
                onChange={() => {}}
                tabIndex={-1}
            />
            <div className="flex flex-wrap items-center gap-1">
                {options.map((option, index) => {
                    const active = selectedIndex >= 0 && index >= selectedIndex;
                    return (
                        <button
                            key={`${option.label}_${option.value}_${index}`}
                            type="button"
                            disabled={props.disabled}
                            aria-label={option.label}
                            aria-pressed={`${props.value}` === `${option.value}`}
                            className={classNames(
                                "h-9 w-9 rounded border text-xl leading-none transition",
                                props.disabled ? "cursor-not-allowed bg-gray-50 text-gray-300 border-gray-200" : "cursor-pointer hover:border-primary-500",
                                props.error ? "border-error-300" : "border-gray-300",
                                active ? "bg-primary-50 text-primary-600 border-primary-500" : "bg-white text-gray-300"
                            )}
                            onClick={(e) => {
                                if (props.disabled) return;
                                props.onChange(e, props.name, option.value);
                            }}
                        >
                            &#9733;
                        </button>
                    );
                })}
            </div>
            <div className={classNames(
                "text-xs font-medium",
                props.disabled ? "text-gray-400" : props.error ? "text-error-500" : "text-gray-500"
            )}>
                {selected?.label || props.placeholder || "Select a rating"}
            </div>
        </div>
    );
}

Rating.propTypes = {
    id: PropTypes.string,
    className: PropTypes.string,
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    value: PropTypes.any,
    errorMessage: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    onChange: PropTypes.func.isRequired,
};

Rating.defaultProps = {
    onChange: () => {
    },
};

Rating.DisplayValue = function (props) {
    return props.value || '\u2014';
}
